import React, { useState, useRef, useEffect } from 'react';

// Fuzzy match: literal substrings score highest (earlier position wins), otherwise falls
// back to in-order subsequence matching so typos/skipped letters still find results.
// Returns -1 for no match at all.
function fuzzyScore(query, text) {
  if (!query) return 0;
  const q = query.toLowerCase();
  const t = text.toLowerCase();
  const idx = t.indexOf(q);
  if (idx !== -1) return 10000 - idx;
  let qi = 0, score = 0, consecutive = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) { qi++; consecutive++; score += consecutive; }
    else consecutive = 0;
  }
  return qi === q.length ? score : -1;
}

/**
 * Generic fuzzy-search dropdown. Pass either:
 *   - `items`: a flat array of options, or
 *   - `groups`: [{ label, items }] to render/search within labelled sections (in the
 *     order given — e.g. client-specific first, then global).
 * Each item defaults to using `.id` and `.name`; override with getId/getLabel.
 */
export default function SearchableSelect({
  value, onChange, items, groups, getId = o => o.id, getLabel = o => o.name,
  placeholder = 'Select…', disabled = false, allowClear = false, clearLabel = 'All',
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  const resolvedGroups = groups || [{ label: null, items: items || [] }];
  const allItems = resolvedGroups.flatMap(g => g.items);
  const selected = allItems.find(o => getId(o) === value);

  const filteredGroups = resolvedGroups.map(g => {
    const scored = g.items
      .map(o => ({ o, score: fuzzyScore(query.trim(), getLabel(o)) }))
      .filter(x => !query.trim() || x.score >= 0);
    if (query.trim()) scored.sort((a, b) => b.score - a.score);
    return { label: g.label, items: scored.map(x => x.o) };
  }).filter(g => g.items.length > 0);

  const hasAnyResults = filteredGroups.length > 0;

  useEffect(() => { if (!open) setQuery(''); }, [open]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(id) { onChange(id); setOpen(false); setQuery(''); }

  function handleInputClick() {
    if (!disabled) { setOpen(true); setTimeout(() => inputRef.current?.select(), 0); }
  }

  const optionStyle = (id) => ({
    padding: '8px 14px', cursor: 'pointer', fontSize: 14,
    background: id === value ? 'var(--accent-light)' : 'transparent',
    color: id === value ? 'var(--accent)' : 'var(--text)',
  });
  const groupLabel = (label) => (
    <div style={{ padding: '6px 12px 2px', fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid var(--border)' }}>{label}</div>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        placeholder={disabled ? 'Loading…' : (open ? 'Type to search…' : (selected ? '' : placeholder))}
        value={open ? query : (selected ? getLabel(selected) : '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onClick={handleInputClick}
        onFocus={handleInputClick}
        style={{ cursor: disabled ? 'not-allowed' : 'text', width: '100%', boxSizing: 'border-box' }}
        autoComplete="off"
      />
      {open && !disabled && (
        <div style={{
          position: 'absolute', zIndex: 999, top: '100%', left: 0, right: 0,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          maxHeight: 280, overflowY: 'auto',
        }}>
          {allowClear && !query.trim() && (
            <div onClick={() => handleSelect('')} style={optionStyle('')}
              onMouseEnter={e => { if (value) e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = !value ? 'var(--accent-light)' : 'transparent'; }}
            >{clearLabel}</div>
          )}
          {filteredGroups.map((g, gi) => (
            <React.Fragment key={g.label || gi}>
              {g.label && groupLabel(g.label)}
              {g.items.map(o => {
                const id = getId(o);
                return (
                  <div key={id} onClick={() => handleSelect(id)} style={optionStyle(id)}
                    onMouseEnter={e => { if (id !== value) e.currentTarget.style.background = 'var(--surface2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = id === value ? 'var(--accent-light)' : 'transparent'; }}
                  >{getLabel(o)}</div>
                );
              })}
            </React.Fragment>
          ))}
          {!hasAnyResults && (
            <div style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text3)' }}>No matches found</div>
          )}
        </div>
      )}
    </div>
  );
}
